/**
 * MobileCommandView (MCV) — mirrors FOM mobile MobileCommandView
 *
 * Exec-only multi-tab dashboard:
 *   - Home:      Centered date/shift, 6-tile 2x3 grid, active posts, on-duty mini-list, notes, pending approvals (exec)
 *   - Team:      Personnel list with badge images → person detail drill-down
 *   - Keys:      Key assignments grid
 *   - Radios:    Radio assignments grid
 *
 *  Header: ← Menu  ·  Command Center  ·  ● Live
 *
 *  Pending user approvals are inline on the Home tab (matches FOM mobile),
 *  visible only to exec roles when there are pending users.
 */
import { useState, useEffect } from 'react'
import { useAuthStore, isExecRole } from '../../stores/auth'
import { useMobileStore } from '../../stores/mobile'
import { useSyncStore, syncColor } from '../../stores/sync'
import { workerGetV1, workerPostV1 } from '../../api/worker'
import { SUPERVISOR_BADGE, OFFICER_BADGE, FCPD_BADGE, KEY_ICON, WALKIE_ICON, WALKIE_ICON_BG, REC_ICON, getPostIcon } from '../../assets'
import { csNumber, getTasksForCS } from '../personnel/personnelHelpers'
import ProfileModal from '../layout/modals/ProfileModal'

/* ─── helpers ─── */
function lastName(n: string) {
  if (!n) return ''
  const s = n.replace(/\s*\([^)]*\)\s*/g, '').trim() || n.trim()
  if (s.includes(',')) return s.split(',')[0].trim()
  const parts = s.split(/\s+/)
  return parts[parts.length - 1]
}
function rolePrefix(rank: string) {
  if (!rank) return ''
  const r = rank.toLowerCase()
  if (r === 'developer') return 'Dev.'
  if (r.includes('director of') || r === 'director') return 'Dir.'
  if (r.includes('assistant')) return 'AD.'
  if (r.includes('lieutenant') || r.startsWith('lt')) return 'Lt.'
  if (r.includes('sergeant') || r.includes('sgt') || r === 'supervisor') return 'Sgt.'
  if (r.includes('dispatch')) return 'Disp.'
  if (r.includes('officer') || r.includes('ofc')) return 'Ofc.'
  if (r === 'oic') return 'OIC'
  if (r === 'fcpd') return ''
  return ''
}
function displayName(name: string, role: string, rank?: string) {
  const pfx = rolePrefix(rank || role)
  const ln = lastName(name)
  return pfx ? `${pfx} ${ln}` : ln
}
function roleLabel(role: string) {
  if (!role) return ''
  const r = role.toLowerCase()
  if (r === 'fcpd') return 'FCPD'
  if (['developer', 'oic'].includes(r) || r.includes('dir') || r.includes('lieu') || r.includes('lt') || r.includes('assistant') || r.includes('serg') || r.includes('sgt') || r === 'supervisor') return 'Supervisor'
  if (r.includes('dispatch')) return 'Dispatcher'
  if (r.includes('officer') || r.includes('ofc')) return 'Officer'
  return role
}
function isGold(role: string) {
  if (!role) return false
  const r = role.toLowerCase()
  return r.includes('dir') || r.includes('ad') || r.includes('lt') || r.includes('lieu') || r.includes('serg') || r.includes('sgt') || r === 'supervisor' || r.includes('developer') || r === 'oic'
}
// Returns the correct badge image for any role including FCPD
function getBadgeImg(role: string) {
  if (role === 'FCPD') return FCPD_BADGE
  return isGold(role) ? SUPERVISOR_BADGE : OFFICER_BADGE
}
function fmtTime(t: string) {
  if (!t) return ''
  const s = String(t)
  if (s.includes(':')) { const p = s.split(':'); return p[0].padStart(2, '0') + ':' + p[1].padStart(2, '0') }
  let padded = s; if (padded.length === 3) padded = '0' + padded
  if (padded.length !== 4) return t
  return padded.substring(0, 2) + ':' + padded.substring(2)
}
function etMinutes() {
  try { const s = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: 'numeric', hour12: false }); const [h, m] = s.split(':'); return parseInt(h) * 60 + parseInt(m) } catch { return new Date().getHours() * 60 + new Date().getMinutes() }
}

/* ─── sub-components ─── */
function PostIconImg({ name, size = 44 }: { name: string; size?: number }) {
  const src = getPostIcon(name)
  if (src) return <img src={src} alt="" style={{ width: size, height: size, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
  return <span style={{ fontSize: size * 0.65, width: size, height: size, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>📍</span>
}

function EquipIconImg({ type, size = 44 }: { type: 'radio' | 'key' | 'recorder'; size?: number }) {
  const src = type === 'key' ? KEY_ICON : type === 'recorder' ? REC_ICON : WALKIE_ICON
  return <img src={src} alt="" style={{ width: size, height: size, objectFit: 'cover', borderRadius: 4 }} />
}

function TimeBadge({ from, to }: { from: string; to: string }) {
  const nowMin = etMinutes()
  let f = String(from).replace(':', ''); if (f.length < 4) f = '0' + f
  let t = String(to).replace(':', ''); if (t.length < 4) t = '0' + t
  const fM = parseInt(f.substring(0, 2)) * 60 + parseInt(f.substring(2))
  const tM = parseInt(t.substring(0, 2)) * 60 + parseInt(t.substring(2))
  let active = false, past = false
  if (fM <= tM) { if (nowMin > 1200 && tM < 720) { active = false; past = false } else { active = nowMin >= fM && nowMin <= tM; past = nowMin > tM } } else { active = nowMin >= fM || nowMin <= tM; past = nowMin > tM && nowMin < fM }
  if (active) return <div style={{ padding: '4px 10px', borderRadius: 20, background: '#166534', fontSize: 11, fontWeight: 700, color: '#4ade80', whiteSpace: 'nowrap', boxShadow: '0 0 8px rgba(74,222,128,0.5)' }}>● Now</div>
  if (past) return <div style={{ padding: '4px 10px', borderRadius: 20, background: 'var(--bg-elevated)', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>✓ Done</div>
  return <div style={{ padding: '4px 10px', borderRadius: 20, background: 'var(--border)', fontSize: 11, fontWeight: 600, color: '#60a5fa', whiteSpace: 'nowrap' }}>Upcoming</div>
}

function SectionBox({ title, color = '#3b82f6', count, children, padded }: { title: string; color?: string; count: number; children: React.ReactNode; padded?: boolean }) {
  return (
    <div style={{ background: 'var(--bg-deep)', borderRadius: 14, border: '1px solid var(--border)', overflow: 'hidden', marginBottom: 12 }}>
      <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border)' }}>
        <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>{title}</span>
        <span style={{ background: `${color}22`, color, fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>{count}</span>
      </div>
      <div style={padded ? { padding: 10 } : undefined}>{children}</div>
    </div>
  )
}

function CountTile({ value, color, label }: { value: number; color: string; label: string }) {
  return (
    <div style={{ background: 'var(--bg-surface)', borderRadius: 14, padding: '16px 8px', textAlign: 'center', border: '1px solid var(--border)' }}>
      <div style={{ fontSize: 28, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, marginTop: 4 }}>{label}</div>
    </div>
  )
}
function EquipTile({ value, label }: { value: number; label: string }) {
  return (
    <div style={{ background: '#0f2a1a', borderRadius: 10, padding: 12, textAlign: 'center', border: '1px solid #166534' }}>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#4ade80' }}>{value}</div>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{label}</div>
    </div>
  )
}

interface PendingUser { callSign: string; name: string; rank: string; email: string; employeeNo: string }
type Tab = 'home' | 'personnel' | 'keys' | 'radios'
interface Props { onBack: () => void }

const TAB_LIST: { id: Tab; icon: string; label: string }[] = [
  { id: 'home',      icon: '🛡️', label: 'Home' },
  { id: 'personnel', icon: '👮', label: 'Team' },
  { id: 'keys',      icon: '🔑', label: 'Keys' },
  { id: 'radios',    icon: '📻', label: 'Radios' },
]

export default function MobileCommandView({ onBack }: Props) {
  const session = useAuthStore(s => s.session)
  const isExec = isExecRole(session?.rank ?? '')
  const [showProfile, setShowProfile] = useState(false)
  const { personnel, secondaryTasks, shiftInfo, notes } = useMobileStore()
  const syncStatus = useSyncStore(s => s.status)

  const [tab, setTab] = useState<Tab>(() => { try { const v = sessionStorage.getItem('fom_mobile_tab') as Tab; return (v && ['home', 'personnel', 'keys', 'radios'].includes(v)) ? v : 'home' } catch { return 'home' } })
  const [selectedPerson, setSelectedPerson] = useState<(typeof personnel)[0] | null>(null)
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([])

  useEffect(() => { try { sessionStorage.setItem('fom_mobile_tab', tab) } catch { /* silent */ } }, [tab])

  async function fetchPending() {
    if (!session?.token || !isExec) return
    try {
      const res = await workerGetV1<{ success: boolean; pending?: PendingUser[] }>('/api/v1/users/pending')
      if (res.success && res.pending) setPendingUsers(res.pending)
    } catch { /* silent */ }
  }
  async function approveUser(cs: string) {
    if (!session?.token) return
    try {
      const res = await workerPostV1<{ success: boolean; error?: string }>('/api/v1/users/approve', { callSign: cs })
      if (res.success) { alert(`${cs} approved!`); fetchPending() }
      else alert(res.error ?? 'Failed')
    } catch { alert('Network error') }
  }
  async function rejectUser(cs: string) {
    if (!confirm(`Reject and remove ${cs}?`)) return
    if (!session?.token) return
    try {
      const res = await workerPostV1<{ success: boolean; error?: string }>('/api/v1/users/reject', { callSign: cs })
      if (res.success) { alert(`${cs} rejected.`); fetchPending() }
      else alert(res.error ?? 'Failed')
    } catch { alert('Network error') }
  }
  useEffect(() => {
    fetchPending()
    const iv = setInterval(fetchPending, 300_000)
    return () => clearInterval(iv)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    try {
      const cs = sessionStorage.getItem('fom_mcv_person')
      if (cs && personnel.length) {
        const found = personnel.find(p => p.callSign === cs)
        if (found) setSelectedPerson(found)
      }
    } catch { /* silent */ }
  }, [personnel])
  function selectPerson(p: (typeof personnel)[0] | null) {
    setSelectedPerson(p)
    try { sessionStorage.setItem('fom_mcv_person', p?.callSign ?? '') } catch { /* silent */ }
  }

  const dotColor = syncColor(syncStatus)
  const onDuty = personnel.filter(p => p.name)
  const supervisors = onDuty.filter(p => isGold(p.role))
  const officers = onDuty.filter(p => !isGold(p.role))
  const withRadio = onDuty.filter(p => p.radio)
  const withKey = onDuty.filter(p => p.key)
  const withRecorder = onDuty.filter(p => p.recorder)

  const card: React.CSSProperties = { background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px', marginBottom: 10 }

  /* ─── PERSON DETAIL ─── */
  if (selectedPerson) {
    const p = selectedPerson
    const isFcpd = p.role === 'FCPD'
    const posts = (p.posts ?? []).filter(x => typeof x === 'object' && !x.isBreak && x.post !== 'Break')
    const breaks = (p.posts ?? []).filter(x => typeof x === 'object' && (x.isBreak || x.post === 'Break'))
    const tasks = getTasksForCS(secondaryTasks, p.callSign)

    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg-deep)', color: 'var(--text-primary)', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
        {/* Static hero header */}
        <div style={{ background: 'linear-gradient(135deg,#0a1830,#1e40af)', padding: '16px 16px 20px', paddingTop: 'calc(16px + env(safe-area-inset-top))', flexShrink: 0 }}>
          <button onClick={() => selectPerson(null)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, padding: '8px 12px', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 12 }}>← Back to list</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ position: 'relative', flexShrink: 0, width: isFcpd ? 88 : 70, height: isFcpd ? 120 : 97 }}>
              <img src={getBadgeImg(p.role)} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              {!isFcpd && (
                <div style={{ position: 'absolute', top: '48%', left: '48%', transform: 'translate(-50%,-50%)', fontWeight: 900, fontSize: 16, color: '#000', fontFamily: 'Arial Black,sans-serif', textAlign: 'center', lineHeight: 1 }}>{csNumber(p.callSign)}</div>
              )}
            </div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{isFcpd ? p.name : displayName(p.name, p.role, p.rank)}</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>{p.callSign} · {roleLabel(p.role)}</div>
              {(p.shiftFrom || p.shiftTo) && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>🕐 {fmtTime(p.shiftFrom)} — {fmtTime(p.shiftTo)}</div>}
            </div>
          </div>
        </div>
        {/* Scrolling body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
          <SectionBox title="Equipment" color="#22c55e" count={[p.radio, p.key, p.recorder].filter(Boolean).length} padded>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {([['radio', 'Radio', p.radio], ['key', 'Key', p.key], ['recorder', 'Recorder', p.recorder]] as const).map(([t, l, v]) => (
                <div key={t} style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {t === 'radio'
                    ? <img src={WALKIE_ICON_BG} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 4 }} />
                    : <EquipIconImg type={t} size={44} />}
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, marginTop: 4, marginBottom: 2 }}>{l}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: v ? '#4ade80' : 'var(--text-hint)' }}>{v || '—'}</div>
                </div>
              ))}
            </div>
          </SectionBox>
          {posts.length > 0 && (
            <SectionBox title="Post Assignments" color="#3b82f6" count={posts.length}>
              {posts.map((post, i) => typeof post === 'object' && (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
                  <PostIconImg name={post.post} size={44} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#f1f5f9' }}>{post.post}</div>
                    {(post.from || post.to) && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{fmtTime(post.from)} — {fmtTime(post.to)}</div>}
                  </div>
                  {post.from && post.to && <TimeBadge from={post.from} to={post.to} />}
                </div>
              ))}
            </SectionBox>
          )}
          {breaks.length > 0 && (
            <SectionBox title="Break" color="#f59e0b" count={breaks.length}>
              {breaks.map((b, i) => typeof b === 'object' && (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 28, flexShrink: 0 }}>☕</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#fbbf24' }}>Break</div>
                    {(b.from || b.to) && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{fmtTime(b.from)} — {fmtTime(b.to)}</div>}
                    {b.coveredBy && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Covered by: {b.coveredBy}</div>}
                  </div>
                  {b.from && b.to && <TimeBadge from={b.from} to={b.to} />}
                </div>
              ))}
            </SectionBox>
          )}
          {tasks.length > 0 && (
            <SectionBox title="Secondary Tasks" color="#a855f7" count={tasks.length}>
              {tasks.map((t, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
                  <PostIconImg name={t.task || t.label} size={44} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#f1f5f9' }}>{t.label}</div>
                    {t.time && <div style={{ fontSize: 12, color: '#a855f7', marginTop: 2 }}>🕐 {t.time}</div>}
                  </div>
                </div>
              ))}
            </SectionBox>
          )}
        </div>
      </div>
    )
  }

  /* ─── HOME TAB ─── */
  function renderHome() {
    type ActivePost = { officer: string; callSign: string; post: string; from: string; to: string }
    const allPosts: ActivePost[] = []
    onDuty.forEach(p => {
      ;(p.posts ?? []).forEach(x => {
        if (typeof x === 'object' && !x.isBreak && x.post !== 'Break') {
          allPosts.push({ officer: p.role === 'FCPD' ? p.name : displayName(p.name, p.role, p.rank), callSign: p.callSign, post: x.post, from: x.from, to: x.to })
        }
      })
    })
    const nowMin = etMinutes()
    const activePosts = allPosts.filter(p => {
      if (!p.from || !p.to) return true
      let f = String(p.from).replace(':', ''); if (f.length < 4) f = '0' + f
      let t = String(p.to).replace(':', ''); if (t.length < 4) t = '0' + t
      const fM = parseInt(f.substring(0, 2)) * 60 + parseInt(f.substring(2))
      const tM = parseInt(t.substring(0, 2)) * 60 + parseInt(t.substring(2))
      if (fM <= tM) return nowMin >= fM && nowMin <= tM
      return nowMin >= fM || nowMin <= tM
    })

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {shiftInfo.date && (
          <div style={{ textAlign: 'center', padding: '12px', color: 'var(--text-muted)', fontSize: 13, fontWeight: 600 }}>
            📅 {shiftInfo.date}{shiftInfo.shift ? ' · ' + shiftInfo.shift : ''}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <CountTile value={onDuty.length}      color="#60a5fa" label="On Duty" />
          <CountTile value={supervisors.length} color="#fbbf24" label="Supervisors" />
          <CountTile value={officers.length}    color="#4ade80" label="Officers" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <EquipTile value={withRadio.length}    label="Radios" />
          <EquipTile value={withKey.length}      label="Keys" />
          <EquipTile value={withRecorder.length} label="Recorders" />
        </div>
        {activePosts.length > 0 && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginTop: 8, marginBottom: 8 }}>📋 Active Posts ({activePosts.length})</div>
            {activePosts.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', marginBottom: 6, borderRadius: 10, background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
                <PostIconImg name={p.post} size={44} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.post}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{p.callSign}: {p.officer}</div>
                </div>
                {p.from && p.to && <TimeBadge from={p.from} to={p.to} />}
              </div>
            ))}
          </div>
        )}
        {onDuty.length > 0 && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginTop: 8, marginBottom: 8 }}>On Duty</div>
            {onDuty.map((p, i) => {
              const isFcpd = p.role === 'FCPD'
              return (
                <button key={i} onClick={() => selectPerson(p)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', marginBottom: 6, borderRadius: 10, background: 'var(--bg-surface)', border: '1px solid var(--border)', color: '#e8edf4', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                  <div style={{ position: 'relative', flexShrink: 0, width: isFcpd ? 50 : 40, height: isFcpd ? 68 : 55 }}>
                    <img src={getBadgeImg(p.role)} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    {!isFcpd && p.badge && (
                      <div style={{ position: 'absolute', top: '48%', left: '48%', transform: 'translate(-50%,-50%)', fontWeight: 900, fontSize: 10, color: '#000', fontFamily: 'Arial Black,sans-serif', textAlign: 'center', lineHeight: 1, pointerEvents: 'none' }}>{String(p.badge).padStart(2, '0')}</div>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{isFcpd ? p.name : displayName(p.name, p.role, p.rank)}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{p.callSign} · {roleLabel(p.role)}</div>
                  </div>
                  <div style={{ fontSize: 14, color: '#334155' }}>›</div>
                </button>
              )
            })}
          </div>
        )}
        {notes.length > 0 && (
          <div style={{ background: 'var(--bg-deep)', borderRadius: 14, border: '1px solid var(--border)', overflow: 'hidden', marginBottom: 12, marginTop: 8 }}>
            <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>Notes / Special Details</span>
              <span style={{ background: 'var(--accent-dim)', color: 'var(--accent)', fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>{notes.length}</span>
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {notes.map((n, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', background: i % 2 === 0 ? 'var(--bg-surface)' : 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: 13, lineHeight: 1.55 }}>
                  <span style={{ color: 'var(--accent)', fontSize: 16, lineHeight: 1.3, flexShrink: 0, fontWeight: 700 }}>•</span>
                  <span style={{ flex: 1 }}>{n}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {isExec && pendingUsers.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 15, marginBottom: 8 }}>⏳ Pending Approvals ({pendingUsers.length})</div>
            {pendingUsers.map((u, i) => (
              <div key={i} style={{ background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--border)', padding: '12px 14px', marginBottom: 8 }}>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{u.name}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>{u.callSign} · {u.rank} · Emp#{u.employeeNo}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={() => approveUser(u.callSign)} style={{ flex: 1, padding: 8, background: '#059669', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>✓ Approve</button>
                  <button onClick={() => rejectUser(u.callSign)} style={{ flex: 1, padding: 8, background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>✕ Reject</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  /* ─── MAIN RENDER ─── */
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg-deep)', color: 'var(--text-primary)', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <div style={{ background: 'var(--bg-deep)', padding: '12px 16px', paddingTop: 'calc(12px + env(safe-area-inset-top))', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={onBack} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8, padding: '6px 12px', color: 'var(--text-muted)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>← Menu</button>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Command Center</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor, animation: 'pulse 2s infinite' }} />
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{syncStatus === 'ok' ? 'Live' : '...'}</span>
          <button onClick={() => setShowProfile(true)} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8, padding: '5px 8px', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer', marginLeft: 4 }} title="My profile">👤</button>
        </div>
      </div>
      {showProfile && <ProfileModal onClose={() => setShowProfile(false)} />}

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 12px' }}>
        {tab === 'home' && renderHome()}

        {tab === 'personnel' && (
          <>
            <div style={{ position: 'sticky', top: -12, zIndex: 10, background: 'var(--bg-deep)', padding: '10px 0 8px', marginBottom: 4, borderBottom: '1px solid var(--border)', marginTop: -12 }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>Team On Duty</span>
              <span style={{ marginLeft: 8, background: '#3b82f622', color: '#3b82f6', fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>{onDuty.length}</span>
            </div>
            {onDuty.length === 0
              ? <div style={{ ...card, textAlign: 'center', padding: 32 }}><div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div><div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No personnel on duty yet</div></div>
              : onDuty.map((p, i) => {
                  const isFcpd = p.role === 'FCPD'
                  return (
                    <button key={i} onClick={() => selectPerson(p)}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 12, background: 'var(--bg-surface)', border: '1px solid var(--border)', color: '#e8edf4', cursor: 'pointer', textAlign: 'left', width: '100%', marginBottom: 8 }}>
                      <div style={{ position: 'relative', flexShrink: 0, width: isFcpd ? 65 : 52, height: isFcpd ? 89 : 72 }}>
                        <img src={getBadgeImg(p.role)} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        {!isFcpd && p.badge && (
                          <div style={{ position: 'absolute', top: '48%', left: '48%', transform: 'translate(-50%,-50%)', fontWeight: 900, fontSize: 13, color: '#000', fontFamily: 'Arial Black,sans-serif', textAlign: 'center', lineHeight: 1, pointerEvents: 'none' }}>{String(p.badge).padStart(2, '0')}</div>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{isFcpd ? p.name : displayName(p.name, p.role, p.rank)}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{p.callSign} · {roleLabel(p.role)}</div>
                        {(p.shiftFrom || p.shiftTo) && <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 2 }}>🕐 {fmtTime(p.shiftFrom)} — {fmtTime(p.shiftTo)}</div>}
                      </div>
                      <div style={{ fontSize: 16, color: '#334155' }}>›</div>
                    </button>
                  )
                })
            }
          </>
        )}

        {tab === 'keys' && (
          <SectionBox title="Key Assignments" color="#10b981" count={withKey.length}>
            {withKey.length === 0
              ? <div style={{ color: 'var(--text-hint)', fontSize: 13, textAlign: 'center', padding: 20 }}>No keys assigned</div>
              : withKey.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
                <EquipIconImg type="key" size={36} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#f1f5f9' }}>{p.callSign}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.role === 'FCPD' ? p.name : displayName(p.name, p.role, p.rank)}</div>
                </div>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#10b981' }}>{p.key}</div>
              </div>
            ))}
          </SectionBox>
        )}

        {tab === 'radios' && (
          <SectionBox title="Radio Assignments" color="#f97316" count={withRadio.length}>
            {withRadio.length === 0
              ? <div style={{ color: 'var(--text-hint)', fontSize: 13, textAlign: 'center', padding: 20 }}>No radios assigned</div>
              : withRadio.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
                <EquipIconImg type="radio" size={36} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#f1f5f9' }}>{p.callSign}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.role === 'FCPD' ? p.name : displayName(p.name, p.role, p.rank)}</div>
                </div>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#f97316' }}>{p.radio}</div>
              </div>
            ))}
          </SectionBox>
        )}
      </div>

      <div style={{ flexShrink: 0, background: 'rgba(15,25,35,0.95)', backdropFilter: 'blur(16px)', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', padding: '8px 0', paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' }}>
        {TAB_LIST.map(t => {
          const active = tab === t.id
          return (
            <button key={t.id} onClick={() => { setTab(t.id); setSelectedPerson(null) }}
              style={{ flex: 1, background: 'none', border: 'none', padding: '6px 0', cursor: 'pointer', textAlign: 'center' }}>
              {t.id === 'radios'
                ? <img src={WALKIE_ICON} alt="" style={{ width: 22, height: 22, objectFit: 'contain', opacity: active ? 1 : 0.5, display: 'inline-block' }} />
                : <div style={{ fontSize: 18, opacity: active ? 1 : 0.5 }}>{t.icon}</div>}
              <div style={{ fontSize: 9, fontWeight: active ? 700 : 500, color: active ? '#60a5fa' : 'var(--text-muted)', marginTop: 2 }}>{t.label}</div>
            </button>
          )
        })}
      </div>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
    </div>
  )
}
